import React, { useEffect, useState } from "react";
import { Box, Alert, Chip, Typography, Stack, IconButton } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector, useDispatch } from "react-redux";
import { customGetAPI } from "services/utils/CustomAPI";
import { setShowDeploymentMessage } from "services/Redux/userManagament";
import moment from "moment";

/**
 * ScheduledMaintenanceNotice Component
 * Displays a maintenance notice with dates, times, and message
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Title of the maintenance notice (default: "Scheduled Maintenance")
 * @param {string} props.badgeLabel - Badge label (default: "New")
 * @param {string} props.startDate - Start date of maintenance (e.g., "January 10th, 2023")
 * @param {string} props.startTime - Start time (e.g., "7:00 AM")
 * @param {string} props.endTime - End time (e.g., "8:00 AM")
 * @param {string} props.timeZone - Timezone (default: "IST")
 * @param {string} props.message - Maintenance message
 * @param {boolean} props.showBadge - Whether to show badge (default: true)
 * @param {string} props.severity - Alert severity level (default: "warning")
 * @param {boolean} props.fetchFromApi - Whether to load data from API (default: true)
 */

const ScheduledMaintenanceNotice = ({
  title = "Scheduled Maintenance",
  badgeLabel = "New",
  startDate,
  startTime,
  endTime,
  timeZone = "IST",
  message = "We apologize for the inconvenience.",
  showBadge = true,
  severity = "warning",
  fetchFromApi = true,
}) => {
  const { domain } = useSelector((state) => state.tokenReducer);
  const { showDeploymentMessage } = useSelector((state) => state?.userReducer);
  const dispatch = useDispatch();
  const [maintenanceData, setMaintenanceData] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [open, setOpen] = useState(true);

  const parseScheduledTime = (timeString) => {
    if (!timeString || typeof timeString !== "string") return null;
    const normalized = timeString.trim();
    const localValue = normalized.endsWith("Z")
      ? normalized.slice(0, -1)
      : normalized;
    const parsed = moment(localValue, moment.ISO_8601, true);
    return parsed.isValid() ? parsed : moment(localValue);
  };

  useEffect(() => {
    if (!showDeploymentMessage) return;

    const fetchMaintenanceData = async () => {
      if (!fetchFromApi || !domain) return;

      try {
        const response = await customGetAPI(
          `${domain}notification-schedules/latest/`,
        );
        let data = response?.responseDetails;

        if (Array.isArray(data)) {
          data = data.length > 0 ? data[0] : null;
        }

        const hasData = data && typeof data === "object";

        if (response.ok && hasData) {
          if (data.scheduled_datetime) {
            const scheduledDateTime = parseScheduledTime(
              data.scheduled_datetime,
            );
            if (scheduledDateTime && scheduledDateTime.isValid()) {
              data.startDate = scheduledDateTime.format("MMMM Do, YYYY");
              data.startTime = scheduledDateTime.format("h:mm A");
              data.timeZone = "IST";
              data.date_time = scheduledDateTime.format("YYYY-MM-DDTHH:mm:ss");
            }
          }
          setMaintenanceData(data);
        } else {
          setMaintenanceData(null);
        }
      } catch (error) {
        console.error("Error fetching maintenance data:", error);
        setMaintenanceData(null);
      } finally {
        setHasFetched(true);
        try {
          if (typeof setShowDeploymentMessage === "function") {
            dispatch(setShowDeploymentMessage(false));
          }
        } catch (err) {
          console.warn("Could not dispatch setShowDeploymentMessage:", err);
        }
      }
    };

    fetchMaintenanceData();
  }, [domain, fetchFromApi, showDeploymentMessage, dispatch]);

  const notice = {
    title,
    badgeLabel,
    startDate,
    startTime,
    endTime,
    timeZone,
    message,
    showBadge,
    severity,
    ...maintenanceData,
  };

  const formattedScheduledTime =
    notice.startDate && notice.startTime
      ? `${notice.startDate}, ${notice.startTime} ${notice.timeZone}`
      : null;

  const normalizedMessage = (() => {
    if (!notice.message) {
      return "";
    }

    const detailsPattern =
      /The portal will be down for maintenance on[\s\S]*?\.(\s*)/i;
    const cleaned = notice.message.replace(detailsPattern, "").trim();

    return cleaned || notice.message;
  })();

  const displayMessage =
    normalizedMessage ||
    "Refresh the application once the maintenance period is completed.";

  const shouldRenderNotice = !fetchFromApi
    ? open && (notice.startDate || notice.message)
    : open && hasFetched && maintenanceData;

  if (!shouldRenderNotice) {
    return null;
  }

  const scheduledValue =
    maintenanceData?.date_time || maintenanceData?.scheduled_datetime;
  const parsedScheduled = scheduledValue
    ? parseScheduledTime(scheduledValue)
    : null;
  const isFutureSchedule = parsedScheduled
    ? parsedScheduled.isSameOrAfter(moment())
    : false;

  return isFutureSchedule ? (
    <Alert
      icon={false}
      severity={notice.severity}
      sx={{
        backgroundColor: "#fff3cd",
        borderRadius: "8px",
        borderLeft: `4px solid #ff9800`,
        py: 1,
        px: 1.5,
        marginTop: "20px",
        marginBottom: "20px",
        width: "100%",
        maxWidth: "95%",
        boxSizing: "border-box",
        overflowWrap: "break-word",
        wordBreak: "break-word",
        whiteSpace: "normal",
        display: "block",
      }}
    >
      <Stack spacing={0.5}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <InfoIcon sx={{ color: "#ff9800", fontSize: 18 }} />
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: "#856404",
                margin: 0,
                lineHeight: 1.1,
              }}
            >
              {notice.title}
            </Typography>
            {notice.showBadge && (
              <Chip
                label={notice.badgeLabel}
                size="small"
                sx={{
                  backgroundColor: "#ff9800",
                  color: "white",
                  fontWeight: 500,
                  height: "22px",
                  fontSize: "0.7rem",
                }}
              />
            )}
          </Box>
          <IconButton
            aria-label="close maintenance notice"
            size="small"
            onClick={() => setOpen(false)}
            sx={{ color: "#856404" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {formattedScheduledTime ? (
          <Typography
            variant="body2"
            sx={{
              color: "#856404",
              margin: 0,
              fontWeight: 500,
              lineHeight: 1.3,
            }}
          >
            Scheduled for <strong>{formattedScheduledTime}</strong>.
          </Typography>
        ) : notice.startDate ? (
          <Typography
            variant="body2"
            sx={{
              color: "#856404",
              margin: 0,
              fontWeight: 500,
              lineHeight: 1.3,
            }}
          >
            The portal will be down for maintenance on{" "}
            <strong>{notice.startDate}</strong> between{" "}
            <strong>
              {notice.startTime} ({notice.timeZone})
            </strong>{" "}
            and{" "}
            <strong>
              {notice.endTime} ({notice.timeZone})
            </strong>
            .
          </Typography>
        ) : null}

        {displayMessage && (
          <Typography
            variant="body2"
            sx={{
              color: "#856404",
              m: 0,
              fontStyle: "italic",
              lineHeight: 1.3,
              whiteSpace: "normal",
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {displayMessage}
          </Typography>
        )}
      </Stack>
    </Alert>
  ) : null;
};

export default ScheduledMaintenanceNotice;
